'use client';
// TAG CONTEXT (14/10/26, tag-search engine): the visitor's search text rides in the `ex_tq` cookie (set by middleware on /search), or in
// ?tq= / #tq= of the pro page. The page HTML is identical for everyone (edge-cache law); AFTER hydration the album and the reviews ask
// /api/tag-order and reorder what is already rendered. No search context = no request, neutral default order.
import { useEffect, useState } from 'react';

export type TagOrder = { q: string; tags: { slug: string; he: string; tier: number }[]; gallery: string[]; reviews: number[] };
const COOKIE = 'ex_tq';

export function readTagQuery(): string {
  try {
    const u = new URL(window.location.href);
    const hash = new URLSearchParams(u.hash.replace(/^#/, ''));
    const fromUrl = u.searchParams.get('tq') || hash.get('tq');
    if (fromUrl) { writeTagQuery(fromUrl); return fromUrl.trim().slice(0, 80); }
    // Ads topic entry (15/10/26): ?tt=<topic-slug> on the ad-group suffix — the slug's dashes are the search words
    // (free-text matching is live; @topic: waits for kw[] in the taxonomy). Same cookie, same downstream order.
    const topic = u.searchParams.get('tt') || hash.get('tt');
    if (topic) { const q = topic.replace(/-/g, ' ').trim().slice(0, 80); if (q) { writeTagQuery(q); return q; } }
    const m = document.cookie.split('; ').find((c) => c.startsWith(COOKIE + '='));
    return m ? decodeURIComponent(m.slice(COOKIE.length + 1)).trim().slice(0, 80) : '';
  } catch { return ''; }
}
export function writeTagQuery(q: string) { try { document.cookie = `${COOKIE}=${encodeURIComponent(q.slice(0, 80))}; path=/; max-age=1800; samesite=lax`; } catch { /* */ } }
export function clearTagQuery() { try { document.cookie = `${COOKIE}=; path=/; max-age=0; samesite=lax`; window.dispatchEvent(new Event('ex-tq-clear')); } catch { /* */ } }

const memo = new Map<string, Promise<TagOrder | null>>();
function fetchOrder(proId: number, q: string): Promise<TagOrder | null> {
  const k = proId + '|' + q;
  if (!memo.has(k)) memo.set(k, fetch(`/api/tag-order?pro=${proId}&q=${encodeURIComponent(q)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  return memo.get(k)!;
}

/** null until a search context exists AND matches something for this pro; then the show-first order */
export function useTagOrder(proId: number): { order: TagOrder | null; reset: () => void } {
  const [order, setOrder] = useState<TagOrder | null>(null);
  useEffect(() => {
    let alive = true;
    const run = () => {
      const q = readTagQuery();
      if (!q) { setOrder(null); return; }
      fetchOrder(proId, q).then((o) => { if (alive) setOrder(o && o.tags.length && (o.gallery.length || o.reviews.length) ? o : null); });
    };
    run();
    const off = () => setOrder(null);
    window.addEventListener('ex-tq-clear', off);
    return () => { alive = false; window.removeEventListener('ex-tq-clear', off); };
  }, [proId]);
  return { order, reset: () => clearTagQuery() };
}

/** small "showing first: X · back to normal order" line shared by the album and the reviews */
export function tagLabel(o: TagOrder): string { return o.tags.filter((t) => t.tier === 2).map((t) => t.he).join(' · ') || o.tags[0]?.he || ''; }
